/**
 * @file        src/modals/FareModal.jsx
 * @description Fare-selection modal — itinerary banner + three fare cards
 *              (radio-select). Ported from `#fareModal` (~line 2136) in the
 *              design HTML.
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-08-17 Claude — MOBILE: viewport-height cap switched from `vh` to `svh`.
 *                          On a phone browser `100vh` is the height with the toolbars
 *                          HIDDEN, so a vh-capped sheet is taller than what is actually
 *                          on screen and its last row — the action buttons — sat under
 *                          the toolbar. `svh` is the always-visible height, so the
 *                          bottom of the sheet is reachable on every device. Desktop is
 *                          unaffected (svh == vh there).
 *   - 2026-07-29 Claude — Brand sweep: the teal→green CTA gradient / solid teal
 *                          button + active-tab fills → brand black (#1C1C1C) with
 *                          white text. Teal is kept for non-button accents (dots,
 *                          ticks, badges, step circles, calendar cells).
 *   - 2026-06-22 Claude — Initial port. `renderFareCards()` from the source
 *                         is replaced by JSX mapping over FARES; the
 *                         currently-selected fare is read from the booking
 *                         store via `selectFare(i)`.
 */
import { useEffect } from 'react';
import { useBooking } from '../store/booking.store.js';
import { inr } from '../lib/format.js';
import { FARES } from '../data/fares.js';
import AirlineLogo from '../components/AirlineLogo.jsx';

export default function FareModal({ open, onClose, payload }) {
  const flight = useBooking((s) => s.flight);
  const fare = useBooking((s) => s.fare);
  const selectFare = useBooking((s) => s.selectFare);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open || !flight) return null;

  const f = flight;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div onClick={onClose} className="absolute inset-0 bg-night/80 backdrop-blur-sm" />
      <div className="relative w-full max-w-4xl max-h-[88svh] overflow-y-auto rounded-3xl border border-line bg-panel no-scrollbar">
        <div className="flex items-center justify-between px-6 py-4 border-b border-line sticky top-0 bg-panel z-10">
          <h2 className="font-display text-xl font-extrabold inline-flex items-center gap-2">
            New Delhi
            <svg viewBox="0 0 24 24" className="w-5 h-5 text-neon"><path fill="currentColor" d="M8 3 4 7l4 4V8h8V6H8zm8 18 4-4-4-4v3H8v2h8z" /></svg>
            Mumbai
          </h2>
          <button onClick={onClose} className="w-9 h-9 rounded-lg bg-night border border-line flex items-center justify-center hover:border-neon cursor-pointer">
            <svg viewBox="0 0 24 24" className="w-5 h-5 text-muted"><path fill="currentColor" d="m12 10.6 4.9-4.9 1.4 1.4-4.9 4.9 4.9 4.9-1.4 1.4-4.9-4.9-4.9 4.9-1.4-1.4 4.9-4.9-4.9-4.9 1.4-1.4z" /></svg>
          </button>
        </div>
        <div className="p-6">
          <div className="mb-5">
            <div className="rounded-xl border border-line bg-night/40 p-4">
              <p className="text-neon text-sm font-semibold mb-2">Depart · Mon, Jun 15 · Duration {f.dur}</p>
              <div className="flex items-center gap-3 text-sm">
                <AirlineLogo code={f.logo} color={f.color} label={f.airline} size={36} />
                <span><b>{f.dep}</b> {f.from} New Delhi Indira Gandhi Intl.</span>
                <span className="text-muted">→</span>
                <span><b>{f.arr}{f.next ? '⁺¹' : ''}</b> {f.to} Mumbai Chhatrapati Shivaji Maharaj Intl.</span>
              </div>
              <p className="text-muted text-xs mt-2">{f.airline} {f.code} · Boeing 737 MAX 8 · Economy class</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {(FARES || []).map((fa, i) => {
              const isSel = fare === fa || fare?.name === fa.name;
              return (
                <label key={i} className={`rounded-2xl border-2 ${isSel ? 'border-neon bg-neon/5' : 'border-line bg-panel/60'} flex flex-col cursor-pointer relative overflow-hidden`}>
                  <div className={`px-3 py-1.5 text-center text-[10px] font-bold tracking-wide whitespace-nowrap ${fa.tag ? 'bg-lime text-onaccent' : 'bg-transparent text-transparent'}`}>{fa.tag || '•'}</div>
                  <div className="p-4 pt-3 flex flex-col flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-display font-bold">{fa.name}</span>
                      <input
                        type="radio"
                        name="faremodal"
                        checked={!!isSel}
                        onChange={() => selectFare(i)}
                        className="accent-[#12798C]"
                      />
                    </div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted mt-3 mb-1">Baggage</p>
                    <ul className="space-y-1 text-xs text-muted">
                      {(fa.baggage || []).map((b, j) => (
                        <li key={j} className="flex items-center gap-1.5">
                          <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-neon"><path fill="currentColor" d="M9 2h6v3h3v17H6V5h3z" /></svg>
                          {b}
                        </li>
                      ))}
                    </ul>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted mt-3 mb-1">Flexibility</p>
                    <ul className="space-y-1 text-xs text-muted flex-1">
                      {(fa.flex || []).map((x, j) => (<li key={j}>{x}</li>))}
                    </ul>
                    <p className="font-display text-lg font-extrabold text-neon mt-3">
                      {inr(fa.price)} <span className="text-muted text-xs line-through font-normal">{inr(fa.was)}</span>
                    </p>
                  </div>
                </label>
              );
            })}
          </div>
          <div className="flex items-center justify-end gap-4 mt-6">
            <span className="text-muted text-sm line-through">{fare ? inr(fare.was) : ''}</span>
            <span className="font-display text-2xl font-extrabold text-neon">{fare ? inr(fare.price) : ''}</span>
            <button
              onClick={() => { onClose?.(); payload?.onContinue?.(); }}
              className="px-8 py-3 rounded-xl bg-[#1C1C1C] text-white font-bold cursor-pointer hover:brightness-125 transition"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
